import { toast } from "sonner";
import { Activity, AlertTriangle, Info, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtAbs, fmtPct, fmtSigned, fmtTol } from "../../lib/calc";
import { CLASSIFICACOES } from "../../lib/constants";
import { buildAnalysis, isStale } from "../../lib/laudo";
import { fmtDate } from "../../lib/model";
import { StepTitle, Panel, ActionButton, ClassBadge, SitBadge, NSelect, TextArea, Field } from "../common/Field";

function Kpi({ label, value, danger, testId }) {
  return (
    <div className="bg-[#050B10] border border-[#1E2F45] rounded-sm p-4" data-testid={testId}>
      <div className="caption-label">{label}</div>
      <div className={cn("font-mono font-bold text-2xl mt-2", danger ? "text-[#FF0000]" : "text-white")}>{value}</div>
    </div>
  );
}

const ICON = { fora: [TriangleAlert, "text-[#FF0000]"], atencao: [AlertTriangle, "text-amber-400"], info: [Info, "text-[#3B8FE0]"] };

export default function Step7Analysis({ insp, update, save }) {
  const a = insp.analysis;
  const stale = a && isStale(insp);
  const generate = async () => {
    const analysis = buildAnalysis(insp);
    update("analysis", analysis);
    await save(true, { analysis });
    toast.success("Análise gerada a partir dos dados registrados");
  };
  const setManual = (patch) => update("analysis", (prev) => {
    const next = { ...prev, ...patch };
    return { ...next, classificacao_final: next.classificacao_manual || next.classificacao || null };
  });

  return (
    <div className="flex flex-col gap-6">
      <StepTitle n={7} title="Análise dos resultados" subtitle="Diagnóstico gerado exclusivamente a partir dos dados registrados. Pontos sem tolerância não são classificados."
        actions={<ActionButton icon={Activity} onClick={generate} testId="btn-gerar-analise">Gerar análise</ActionButton>} />
      {!a && <Panel testId="analysis-empty"><p className="text-slate-400 text-sm">Nenhuma análise gerada. Clique em <b className="text-[#FFD500]">GERAR ANÁLISE</b> após registrar as medições.</p></Panel>}
      {a && (
        <>
          {stale && <div className="border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm px-4 py-3 rounded-sm" data-testid="analysis-stale">Os dados foram alterados após a última análise ({fmtDate(a.generated_at)}). Gere a análise novamente.</div>}
          <Panel title="Resultado do alinhamento" testId="panel-result">
            <div className="flex flex-col lg:flex-row gap-6 lg:items-center justify-between">
              <div>
                <div className="caption-label mb-2">Classificação</div>
                <ClassBadge large value={a.classificacao_final} testId="analysis-classificacao" />
                {!a.classificacao_final && <div className="font-mono text-sm text-amber-300 mt-2" data-testid="analysis-status-msg">{a.statusMsg}</div>}
                {a.classificacao_manual && <div className="font-mono text-[11px] text-slate-400 mt-2">DEFINIDA MANUALMENTE · AUTOMÁTICA: {a.classificacao || a.statusMsg}</div>}
              </div>
              <div className="font-mono text-xs text-slate-500">Gerada em {fmtDate(a.generated_at)}</div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
              <Kpi label="Pontos medidos" value={`${a.counts.medidos}/${a.counts.total}`} testId="kpi-medidos" />
              <Kpi label="Fora da tolerância" value={a.counts.fora} danger={a.counts.fora > 0} testId="kpi-fora" />
              <Kpi label="Maior desvio" value={a.maior ? fmtAbs(a.maior.valor) : "—"} danger={a.counts.fora > 0} testId="kpi-maior" />
              <Kpi label="Local do maior desvio" value={a.maior ? a.maior.ponto : "—"} testId="kpi-local" />
              <Kpi label="Em atenção" value={a.counts.atencao} testId="kpi-atencao" />
              <Kpi label="Desvio médio" value={fmtAbs(a.media)} testId="kpi-media" />
              <Kpi label="Conformidade" value={fmtPct(a.conformidade)} testId="kpi-conformidade" />
              <Kpi label="Tipo principal de anomalia" value={<span className="text-sm">{a.tipoPrincipal}</span>} testId="kpi-tipo" />
            </div>
          </Panel>
          <div className="grid xl:grid-cols-2 gap-6">
            <Panel title="Diagnóstico técnico" subtitle="Constatações baseadas nos dados" testId="panel-findings">
              {!a.findings.length && <p className="text-sm text-slate-400">Nenhuma constatação fora dos parâmetros.</p>}
              <ul className="flex flex-col gap-2">
                {a.findings.map((f, i) => { const [Icon, cls] = ICON[f.nivel]; return <li key={i} className="flex gap-2 text-sm text-slate-200" data-testid={`finding-${i}`}><Icon size={16} className={cn("shrink-0 mt-0.5", cls)} />{f.texto}</li>; })}
              </ul>
            </Panel>
            <Panel title="Avisos e dados pendentes" subtitle="Informações insuficientes / tolerâncias não informadas" testId="panel-warnings">
              {!a.warnings.length && <p className="text-sm text-slate-400">Sem avisos.</p>}
              <ul className="flex flex-col gap-2">{a.warnings.map((w, i) => <li key={i} className="text-sm text-amber-200 border-l-2 border-amber-500 pl-3" data-testid={`warning-${i}`}>{w}</li>)}</ul>
              <div className="mt-4 font-mono text-[11px] text-slate-500">FONTE DAS TOLERÂNCIAS: {a.fontes}</div>
            </Panel>
          </div>
          <Panel title="Pontos analisados" testId="panel-analysis-points">
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono">
                <thead><tr className="caption-label text-left border-b border-[#1E2F45]">{["Ponto", "Descrição", "ΔX", "ΔY", "ΔZ", "Resultante", "Dif. linear", "Tolerância", "Situação"].map((h) => <th key={h} className="px-2 py-2 font-normal">{h}</th>)}</tr></thead>
                <tbody>{a.pontos.map((p) => (
                  <tr key={p.numero} className="border-b border-[#1E2F45]/60">
                    <td className="px-2 py-1.5 text-[#FFD500] font-bold">{p.numero}</td><td className="px-2 py-1.5 text-slate-300">{p.descricao || "—"}</td>
                    <td className="px-2 py-1.5">{fmtSigned(p.dx)}</td><td className="px-2 py-1.5">{fmtSigned(p.dy)}</td><td className="px-2 py-1.5">{fmtSigned(p.dz)}</td>
                    <td className="px-2 py-1.5">{fmtAbs(p.resultant)}</td><td className="px-2 py-1.5">{fmtSigned(p.diff)}</td><td className="px-2 py-1.5">{fmtTol(p.tol)}</td>
                    <td className="px-2 py-1.5"><SitBadge situacao={p.situacao} /></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Panel>
          <Panel title="Classificação pelo responsável técnico" subtitle="Opcional — substitui a classificação automática e é registrada no laudo com justificativa." testId="panel-manual">
            <div className="grid md:grid-cols-[360px_1fr] gap-4">
              <Field label="Classificação manual"><NSelect testId="analysis-manual-select" value={a.classificacao_manual} onChange={(v) => setManual({ classificacao_manual: v })} options={CLASSIFICACOES} placeholder="Usar classificação automática" /></Field>
              <Field label="Justificativa"><TextArea testId="analysis-manual-justificativa" rows={2} value={a.justificativa_manual} onChange={(v) => setManual({ justificativa_manual: v })} /></Field>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
